import {inject, Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {tap} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
import {ConfigService} from '../../../config/config.service';
import {CartItem} from '../../models/cart/cart-item';
import {Address} from '../../models/address/address';

@Injectable({
  providedIn: 'root',
})
export class CartService {


  private cartCountSubject = new BehaviorSubject<number>(0);
  public cartCount$ = this.cartCountSubject.asObservable();

  private readonly http = inject(HttpClient);
  private readonly configService = inject(ConfigService);

  private readonly baseUrl: string;

  constructor() {
    this.baseUrl = this.configService.get().END_POINT;

    // Attempt to load cart count initially if logged in
    // This assumes the token is already in local storage if logged in.
    this.refreshCartCount();
  }

  refreshCartCount(): void {
    const token = localStorage.getItem('accessToken');
    if (token || sessionStorage.getItem('accessToken')) {
      this.getCartByUserId().subscribe({
        next: (carts: any) => {
          if (carts && carts.length > 0 && carts[0].cartItems) {
            const count = carts[0].cartItems.reduce((acc: number, item: any) => acc + item.quantity, 0);
            this.cartCountSubject.next(count);
          } else {
            this.cartCountSubject.next(0);
          }
        },
        error: () => this.cartCountSubject.next(0)
      });
    }
  }

  //add to cart by user
  addToCart(payload: any): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/api/cartItem/add`, payload
    ).pipe(
      tap(() => this.refreshCartCount())
    );
  }

  //update quantity
  updateQuantity(payload: any): Observable<any> {
    return this.http.put(
      `${this.baseUrl}/api/cartItem/update`, payload
    ).pipe(
      tap(() => this.refreshCartCount())
    );
  }

  //get cart by user id (derived from auth token)
  getCartByUserId(): Observable<any> {
    return this.http.get(
      `${this.baseUrl}/api/cart/getByUserId`
    ).pipe(
      tap((carts: any) => {
        if (carts && carts.length > 0 && carts[0].cartItems) {
          const count = carts[0].cartItems.reduce((acc: number, item: any) => acc + item.quantity, 0);
          this.cartCountSubject.next(count);
        } else {
          this.cartCountSubject.next(0);
        }
      })
    );
  }

  //get cart items by cart id
  getCartItems(cartId: number): Observable<CartItem[]> {
    return this.http.get<CartItem[]>(
      `${this.baseUrl}/api/cartItem/getByCart/${cartId}`
    );
  }

  //remove cart item by cart item id
  removeCartItem(cartItemId: number): Observable<any> {
    return this.http.delete(
      `${this.baseUrl}/api/cartItem/remove/${cartItemId}`
    );
  }


  //add address by customer
  addAddress(payload: any): Observable<Address[]> {
    return this.http.post<Address[]>(`${this.baseUrl}/api/address-details/save`, payload);
  }

  //get all address
  getAllAddressByUserId(): Observable<Address[]> {
    return this.http.get<Address[]>(`${this.baseUrl}/api/address-details/getAll`);
  }
}
